import {
    getInstagramRelatedHashtagData,
    getInstagramRelatedHashtagDataInPairs,
    getInstagramRelatedHashtagGroupData,
} from 'utils/api/instagram.api';
import { useInfiniteQuery, useQuery } from 'react-query';
import { useFetchInstagramRelatedAffinityRoots } from 'utils/hooks/api';

export const getQueryKeyRelatedHashtagData = ({
    order_by,
    ascending,
    categories,
    brands,
    hashtags,
    countries,
    affinity_group,
    search,
    per_page,
}) => {
    return [
        'InstagramRelatedHashtagData',
        {
            order_by,
            ascending,
            category: categories,
            brand: brands,
            hashtag: hashtags,
            country: countries,
            affinity_group,
            search,
            per_page,
        },
    ];
};

const useInstagramRelatedHashtagData = ({
    per_page = 500,
    order_by = 'non_cum_avg_weekly_likes',
    ascending = false,
    brands,
    categories,
    hashtags,
    countries,
    affinity_group,
    search,
    enabled = true,
}) => {
    const queryFn = ({ queryKey, pageParam = 1 }) =>
        getInstagramRelatedHashtagData({
            data: {
                ...queryKey[1],
                page: pageParam,
            },
        }).then((resp) => {
            resp?.data?.forEach((d) => {
                if (d?.hashtag) d.name = d.hashtag;
                if (d?.affinity_group) d.parentName = d.affinity_group;
                if (d?.brand_name) d.brandName = d.brand_name;
            });
            return resp;
        });
    const queryKey = getQueryKeyRelatedHashtagData({
        order_by,
        ascending,
        categories,
        brands,
        hashtags,
        countries,
        affinity_group,
        search,
        per_page,
    });
    const query = useInfiniteQuery(queryKey, queryFn, {
        enabled,
        staleTime: Infinity,
        getNextPageParam: (lastPage) => lastPage?.next_page ?? undefined,
        keepPreviousData: true,
    });
    const getQueryData = async () => {
        const currentPages = query?.data?.pages || [];
        const proms = [];
        for (
            let i = currentPages.length + 1;
            i <= query?.data?.pages[0]?.last_page;
            i++
        ) {
            proms.push(queryFn({ queryKey, pageParam: i }));
        }
        const restPages = await Promise.all(proms);
        return [...currentPages, ...restPages].map(({ data }) => data).flat();
    };
    return {
        query,
        queryKey,
        getQueryData,
    };
};

const getQueryKeyRelatedHashtagGroupData = ({
    order_by,
    ascending,
    categories,
    brands,
    countries,
}) => {
    return [
        'InstagramRelatedHashtagGroupData',
        {
            order_by,
            ascending,
            category: categories,
            brand: brands,
            country: countries,
        },
    ];
};

export const useInstagramRelatedHashtagGroupData = ({
    order_by = 'non_cum_avg_weekly_likes',
    ascending = false,
    brands,
    categories,
    countries,
    enabled = true,
}) => {
    const queryFn = ({ queryKey }) =>
        getInstagramRelatedHashtagGroupData({
            data: {
                ...queryKey[1],
            },
        }).then((resp) => {
            resp?.data?.forEach((d) => {
                if (d?.affinity_group) d.name = d.affinity_group;
                // lowercase related category group names for consistency with other data sources
                if (d?.group) d.group = d.group.toLowerCase();
            });
            return resp;
        });
    const queryKey = getQueryKeyRelatedHashtagGroupData({
        order_by,
        ascending,
        categories,
        brands,
        countries,
    });
    return useQuery(queryKey, queryFn, {
        enabled,
        staleTime: Infinity,
    });
};

// Reads from the new IG `related_affinity_groups` endpoint (returns a flat
// array of `{ name, id, volume }`, lower-cased name, sorted by volume desc).
// The legacy `useInstagramRelatedHashtagGroupData` source hit the dropped
// `data_store_related_hashtag_group_*` tables.
const shapeInstagramAffinityRoot = ({ name, volume }) => ({
    label: name,
    value: name,
    volume,
});

export const useFetchMostPopularInstagramAffinityRoots = (props) => {
    const { data: initialData, ...restQuery } =
        useFetchInstagramRelatedAffinityRoots(props);
    const data = (initialData || [])
        .slice(0, 6)
        .map(shapeInstagramAffinityRoot);
    return { data, ...restQuery };
};

export const useFetchLessPopularInstagramAffinityRoots = (props) => {
    const { data: initialData, ...restQuery } =
        useFetchInstagramRelatedAffinityRoots(props);
    const data = (initialData || []).slice(6).map(shapeInstagramAffinityRoot);
    return { data, ...restQuery };
};

export const useInstagramRelatedHashtagDataInPairs = ({
    per_page = 500,
    order_by = 'non_cum_avg_weekly_likes',
    ascending = false,
    brands,
    categories,
    hashtags,
    countries,
    week,
    year,
    enabled = true,
    fetch_in_pairs = true,
    placeholderData,
    keepPreviousData = false,
}) => {
    const queryFn = ({ queryKey, pageParam = 1 }) =>
        getInstagramRelatedHashtagDataInPairs({
            data: {
                ...queryKey[1],
                page: pageParam,
            },
        });

    const queryKey = [
        'InstagramRelatedHashtagDataInPairs',
        {
            order_by,
            ascending,
            category: categories,
            brand: brands,
            hashtag: hashtags,
            country: countries,
            week,
            year,
            per_page,
            fetch_in_pairs,
        },
    ];

    const query = useInfiniteQuery(queryKey, queryFn, {
        enabled,
        staleTime: Infinity,
        getNextPageParam: (lastPage) => lastPage?.next_page ?? undefined,
        keepPreviousData,
        placeholderData: placeholderData?.length
            ? () => ({
                  pages: [
                      {
                          data: placeholderData,
                          total_records: placeholderData?.length || 0,
                          last_page: 1,
                          messages: null,
                          next_page: null,
                          page: 1,
                          per_page,
                          prev_page: null,
                      },
                  ],
                  pageParams: [1],
              })
            : undefined,
    });

    return {
        query,
        queryKey,
    };
};

export default useInstagramRelatedHashtagData;
